import { Menu, Smartphone, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { APP_STORE_URL, PLAY_STORE_URL, detectPlatform } from "./app-links";

type GetAppButtonProps = {
  size?: "sm" | "default" | "lg";
  className?: string;
  /** Header placement: both store buttons move into a menu on narrow screens. */
  compact?: boolean;
};

type StoreButtonsProps = { size: GetAppButtonProps["size"]; className?: string };

function StoreButtons({ size, className }: StoreButtonsProps) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <Button asChild size={size} className="rounded-full bg-[#111217] text-white hover:bg-[#2a2b33]">
        <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
          App Store
        </a>
      </Button>
      <Button asChild size={size} variant="outline" className="rounded-full border-[#d9dcd7] bg-white hover:bg-[#f3f4f2]">
        <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
          Google Play
        </a>
      </Button>
    </div>
  );
}

/** Both store buttons inline from sm up; below that, behind a menu button. */
function HeaderStoreMenu({ size, className }: StoreButtonsProps) {
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!menu.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <>
      <StoreButtons size={size} className={cn("shrink-0 flex-nowrap max-sm:hidden", className)} />
      <div ref={menu} className="relative sm:hidden">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Get the Glassly App"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(value => !value)}
          className="rounded-full hover:bg-[#f3f4f2]"
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </Button>
        {open ? (
          <div id={panelId} className="absolute right-0 top-full mt-2 w-48 rounded-2xl border border-[#e4e6e2] bg-white p-2 shadow-lg">
            <StoreButtons size={size} className="flex-col items-stretch" />
          </div>
        ) : null}
      </div>
    </>
  );
}

/** The web store's stand-in for Install: on a phone its own store, elsewhere both. */
export function GetAppButton({ size = "default", className, compact }: GetAppButtonProps) {
  const platform = detectPlatform();
  if (platform === "other") {
    return compact ? <HeaderStoreMenu size={size} className={className} /> : <StoreButtons size={size} className={className} />;
  }
  const href = platform === "android" ? PLAY_STORE_URL : APP_STORE_URL;
  return (
    <Button asChild size={size} className={cn("rounded-full bg-[#111217] text-white hover:bg-[#2a2b33]", className)}>
      <a href={href} target="_blank" rel="noreferrer">
        <Smartphone className="size-4" />
        {compact ? "Get the app" : "Get the Glassly App"}
      </a>
    </Button>
  );
}
